package dev.krno.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

/**
 * Mouse-position-driven 3D tilt on speaker and extras cards. Reads the cursor's
 * position within each card, writes `--rx` / `--ry` CSS custom properties on
 * the card element, and the CSS side (see `.spk`, `.card`) composes them with
 * the existing `rotate()` / `translateY()` transforms via `perspective()`.
 *
 * Skipped on touch-only devices — no cursor to track.
 */
private const val MAX_TILT_DEG = 8.0
private const val TILT_SELECTOR = ".spk, .plogo"

@Composable
fun CardTilt() {
    DisposableEffect(Unit) {
        // No point running this on touch devices — matchMedia guards.
        val supportsHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches
        if (!supportsHover) return@DisposableEffect onDispose { }

        val cards = document.querySelectorAll(TILT_SELECTOR)
        val handlers = mutableListOf<Triple<HTMLElement, String, (Event) -> Unit>>()

        for (i in 0 until cards.length) {
            val card = cards.item(i) as? HTMLElement ?: continue

            val onMove: (Event) -> Unit = { evt ->
                val me = evt.asDynamic()
                val rect = card.getBoundingClientRect()
                val x = (me.clientX as Number).toDouble() - rect.left
                val y = (me.clientY as Number).toDouble() - rect.top
                val cx = rect.width / 2.0
                val cy = rect.height / 2.0
                // ratio in [-1, 1]
                val rx = if (cy == 0.0) 0.0 else (cy - y) / cy
                val ry = if (cx == 0.0) 0.0 else (x - cx) / cx
                card.style.setProperty("--rx", "${rx * MAX_TILT_DEG}deg")
                card.style.setProperty("--ry", "${ry * MAX_TILT_DEG}deg")
            }
            val onLeave: (Event) -> Unit = {
                card.style.setProperty("--rx", "0deg")
                card.style.setProperty("--ry", "0deg")
            }

            card.addEventListener("mousemove", onMove)
            card.addEventListener("mouseleave", onLeave)
            handlers += Triple(card, "mousemove", onMove)
            handlers += Triple(card, "mouseleave", onLeave)
        }

        onDispose {
            handlers.forEach { (el, evt, handler) ->
                el.removeEventListener(evt, handler)
            }
        }
    }
}
